import { useEffect, useRef, useCallback } from 'react';
import type { Lead } from '../../../types';
import { campaignInitialLetter, type FunilIaStageSlug } from '../funilIaStage';
import type { BallCommand, PhysicsBall } from '../engine/types';
import { resolveSuckHandoffTarget } from '../handoffBinHelpers';

/** Delay between initial ball spawns for visual stagger */
const INITIAL_STAGGER_MS = 80;
/** Debounce window for command generation */
const DEBOUNCE_MS = 100;
/** Handoff timer delay (reativação) */
const HANDOFF_DELAY_MS = 5000;

interface UseLeadBallSyncArgs {
  grouped: Record<FunilIaStageSlug, Lead[]>;
  /**
   * Conjunto completo de leads do funil ANTES do filtro por `aiFunnelHandoffAt`.
   * Quando um lead some de `grouped` mas continua aqui com `aiFunnelHandoffAt`
   * setado, sabemos que foi um handoff real do backend — dispara animação
   * `suck` (rastro de cometa) pro bin apropriado. Sem isso, lead simplesmente
   * desaparecia (animation: 'fade').
   */
  allFunnelLeads: Lead[];
  balls: Map<string, PhysicsBall>;
  campaignNames: Record<string, string>;
  onCommand: (cmd: BallCommand) => void;
  onHandoffComplete: (leadId: string) => void;
}

/**
 * Detects diffs between grouped leads and existing physics balls,
 * emitting BallCommands for the physics engine to process.
 *
 * Handles:
 * - New leads → create command (with stagger on initial load)
 * - Removed leads → fade command
 * - Stage changes → transition command
 * - iaRunState changes → updateVisual command
 * - Message activity → impulse command
 * - Handoff trigger (reativação) → remove command after 5s timer
 */
export function useLeadBallSync({
  grouped,
  allFunnelLeads,
  balls,
  campaignNames,
  onCommand,
  onHandoffComplete,
}: UseLeadBallSyncArgs): void {
  const prevLeadStatesRef = useRef<Map<string, {
    lane: FunilIaStageSlug;
    iaRunState?: string;
    isReativacao?: boolean;
    updatedAt?: string;
  }>>(new Map());
  const isInitialLoadRef = useRef(true);
  const handoffTimersRef = useRef<Map<string, number>>(new Map());
  const debounceRef = useRef<number | null>(null);
  const onCommandRef = useRef(onCommand);
  onCommandRef.current = onCommand;
  const onHandoffCompleteRef = useRef(onHandoffComplete);
  onHandoffCompleteRef.current = onHandoffComplete;

  // Cleanup handoff timers on unmount
  useEffect(() => {
    const timers = handoffTimersRef.current;
    const debounce = debounceRef;
    return () => {
      timers.forEach((tid) => window.clearTimeout(tid));
      timers.clear();
      if (debounce.current !== null) window.clearTimeout(debounce.current);
    };
  }, []);

  // Note: processSync depends on `allFunnelLeads` (lookup for handoff detection),
  // re-running when it changes.
  const processSync = useCallback(() => {
    const prevStates = prevLeadStatesRef.current;
    const currentLeadIds = new Set<string>();
    let staggerIndex = 0;
    const isInitial = isInitialLoadRef.current;

    // Process all leads across all stages
    const stages: FunilIaStageSlug[] = ['inicio', 'em_negociacao_ia', 'proposta_ia'];
    for (const stage of stages) {
      const leads = grouped[stage] ?? [];
      for (const lead of leads) {
        const leadId = lead.leadId;
        currentLeadIds.add(leadId);

        const prevState = prevStates.get(leadId);
        const currentLane = stage;

        if (!prevState) {
          // New lead — create ball
          const campName = campaignNames[lead.inboundCampaignId ?? ''] ?? '';
          const initial = campaignInitialLetter(campName);
          onCommandRef.current({
            type: 'create',
            leadId,
            lane: currentLane,
            initial,
            leadName: lead.nome || lead.telefone || '?',
            campaignId: lead.inboundCampaignId ?? '',
            staggerDelay: isInitial ? staggerIndex * INITIAL_STAGGER_MS : 0,
            startThinking: lead.iaRunState === 'running',
          });
          staggerIndex++;
        } else {
          // Existing lead — check for changes

          // Stage change → transition
          if (prevState.lane !== currentLane) {
            onCommandRef.current({
              type: 'transition',
              leadId,
              fromLane: prevState.lane,
              toLane: currentLane,
            });
          }

          // iaRunState change → updateVisual + impulse
          if (prevState.iaRunState !== lead.iaRunState) {
            const thinking = lead.iaRunState === 'running';
            onCommandRef.current({
              type: 'updateVisual',
              leadId,
              thinking,
            });
            // IA responding = message activity → impulse
            if (thinking) {
              onCommandRef.current({ type: 'impulse', leadId });
            }
          }

          // updatedAt change (new message activity) → impulse + reafirmar thinking se IA a correr
          if (prevState.updatedAt && lead.updatedAt && prevState.updatedAt !== lead.updatedAt) {
            onCommandRef.current({ type: 'impulse', leadId });
            if (lead.iaRunState === 'running') {
              onCommandRef.current({ type: 'updateVisual', leadId, thinking: true });
            }
          }
        }

        // Update prev state
        prevStates.set(leadId, {
          lane: currentLane,
          iaRunState: lead.iaRunState,
          isReativacao: lead.isReativacao,
          updatedAt: lead.updatedAt,
        });

        // Handoff trigger: reativação in any lane
        if (lead.isReativacao && !handoffTimersRef.current.has(`reativacao:${leadId}`)) {
          const tid = window.setTimeout(() => {
            handoffTimersRef.current.delete(`reativacao:${leadId}`);
            onCommandRef.current({
              type: 'remove',
              leadId,
              animation: 'suck',
              suckTargetBin: resolveSuckHandoffTarget(lead),
            });
          }, HANDOFF_DELAY_MS);
          handoffTimersRef.current.set(`reativacao:${leadId}`, tid);
        }

        // Lane `proposta_ia` agora é rotulada "Coletando dados" (slug mantido por compat).
        // Handoff humano deve ocorrer via `handoffSignal=proposal_ready` do CRM Manager
        // (após coletar dados), nunca por timer automático nesta lane.
      }
    }

    // Detect removed leads
    const leadsById = new Map(allFunnelLeads.map((l) => [l.leadId, l] as const));
    prevStates.forEach((_, leadId) => {
      if (!currentLeadIds.has(leadId)) {
        const ball = balls.get(leadId);
        if (ball && ball.visualState !== 'sucked' && ball.visualState !== 'exiting') {
          // Distinguir handoff real (lead segue em allFunnelLeads mas filtrado
          // por aiFunnelHandoffAt) de remoção arbitrária (lead foi deletado).
          // Handoff → animação `suck` (rastro de cometa) pro bin apropriado.
          // Remoção → `fade` (some discreto).
          const liveLead = leadsById.get(leadId);
          if (liveLead?.aiFunnelHandoffAt) {
            onCommandRef.current({
              type: 'remove',
              leadId,
              animation: 'suck',
              suckTargetBin: resolveSuckHandoffTarget(liveLead),
            });
          } else {
            onCommandRef.current({ type: 'remove', leadId, animation: 'fade' });
          }
        }
        prevStates.delete(leadId);

        // Clean up handoff timer (reativação)
        const tid = handoffTimersRef.current.get(`reativacao:${leadId}`);
        if (tid !== undefined) {
          window.clearTimeout(tid);
          handoffTimersRef.current.delete(`reativacao:${leadId}`);
        }
      }
    });

    if (isInitial && staggerIndex > 0) {
      isInitialLoadRef.current = false;
    }
  }, [grouped, allFunnelLeads, balls, campaignNames]);

  useEffect(() => {
    // Debounce to avoid jitter on WS burst
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      processSync();
    }, DEBOUNCE_MS);
  }, [processSync]);
}
